import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ArrowRight, Blocks, Check, Fence, Framer, Hammer, Handshake, Layers, Lightbulb, MapPin, Mail, Phone, Ruler, ShieldCheck } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import EstimateForm from '@/components/EstimateForm'
import ProjectGallery from '@/components/ProjectGallery'
import { CONTACT, HERO_IMAGE, SERVICES, type Service } from '@/data/content'

const SERVICE_ICONS: Record<Service['icon'], LucideIcon> = {
  patio: Layers,
  wall: Blocks,
  pergola: Framer,
  light: Lightbulb,
  fence: Fence,
  design: Ruler,
}

function Hero() {
  return (
    <section
      id="top"
      className="relative mx-auto max-w-6xl px-5 pb-10 pt-6 sm:px-8 lg:pb-14 lg:pt-10"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 -top-10 h-16 bg-gradient-to-b from-forest-950/[0.06] to-transparent"
      />
      <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
        <div>
          <p className="inline-flex items-center gap-2 rounded-full border border-forest-950/10 bg-bone-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-forest-800">
            <MapPin className="h-3.5 w-3.5 text-pine" />
            {CONTACT.region}
          </p>

          <h1 className="mt-5 font-display text-[2.05rem] font-semibold leading-[1.05] tracking-tight text-forest-950 sm:text-[2.9rem] lg:text-[3.15rem]">
            Built Right from
            <br className="hidden sm:block" /> the Ground Up.
          </h1>

          <p className="mt-4 max-w-xl text-base leading-relaxed text-forest-950/70 sm:text-[1.05rem]">
            Fourteen years of hands-on hardscaping, outdoor carpentry, and 12-volt landscape lighting
            across North Carolina. We build the base first, so the finish holds for decades.
          </p>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href="#contact"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-pine px-7 py-3.5 text-sm font-semibold text-bone-50 transition hover:bg-forest-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine focus-visible:ring-offset-2 focus-visible:ring-offset-bone-100"
            >
              Request an Estimate
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
            </a>
            <a
              href="#gallery"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-forest-950/20 px-7 py-3.5 text-sm font-semibold text-forest-950 transition hover:border-forest-950/40 hover:bg-bone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine focus-visible:ring-offset-2 focus-visible:ring-offset-bone-100"
            >
              View Gallery
            </a>
          </div>
        </div>

        <div className="relative">
          <div className="overflow-hidden rounded-[2rem] border border-forest-950/10 bg-bone-50 p-2 shadow-[0_28px_60px_-32px_rgba(11,26,18,0.4)]">
            <img
              src={HERO_IMAGE}
              alt="Stone patio with low-voltage wall and path lighting at dusk"
              className="h-[24rem] w-full rounded-[1.6rem] object-cover object-center sm:h-[30rem] lg:h-[34rem]"
              loading="eager"
            />
          </div>
        </div>
      </div>
    </section>
  )
}

function About() {
  return (
    <section id="about" className="scroll-mt-24 bg-forest-950 py-16 text-bone-50 lg:py-24">
      <div className="mx-auto grid max-w-6xl gap-10 px-5 sm:px-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-pine-400">About the craft</p>
          <h2 className="mt-5 font-display text-3xl font-semibold leading-[1.1] tracking-tight sm:text-[2.75rem]">
            Craftsmanship Without the Shortcuts.
          </h2>
        </div>
        <div className="space-y-6">
          <p className="text-base leading-[1.75] text-bone-100/80">
            With over 14 years of hands-on experience across Central and Eastern North Carolina,
            Trimmed Living specializes in custom landscaping & hardscaping, outdoor carpentry, and 12-volt landscape
            lighting.
          </p>
          <p className="text-base leading-[1.75] text-bone-100/80">
            We believe quality starts beneath the surface — never cutting corners on preparation and
            laying a solid, dependable foundation so your investment stays level, drains properly, and
            lasts.
          </p>
          <ul className="grid gap-3 border-t border-bone-50/15 pt-6 sm:grid-cols-3">
            {[
              { icon: ShieldCheck, label: 'Solid foundations & proper site prep' },
              { icon: Handshake, label: 'Family-owned & hands-on on every project' },
              { icon: Hammer, label: 'Custom outdoor living & carpentry' },
            ].map(item => (
              <li key={item.label} className="flex items-start gap-2.5 text-sm text-bone-100/70">
                <item.icon className="mt-0.5 h-4 w-4 shrink-0 text-pine-400" />
                {item.label}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

function Services() {
  return (
    <section id="services" className="scroll-mt-24 border-y border-bone-300 bg-bone-50 py-16 lg:py-24">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <p className="inline-flex rounded-full bg-pine/10 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-pine ring-1 ring-inset ring-pine/25">
            Custom Outdoor Living &amp; Hardscapes
          </p>
          <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-forest-950 sm:text-[2.5rem]">
            What We Build &amp; Install.
          </h2>
          <p className="mt-4 text-[1.05rem] leading-relaxed text-forest-950/65">
            Turnkey hardscapes, outdoor structures, and grounds enhancement across Central and
            Eastern North Carolina.
          </p>
        </div>

        <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map(service => {
            const Icon = SERVICE_ICONS[service.icon]
            return (
              <li
                key={service.name}
                className="group flex flex-col gap-4 rounded-2xl border border-bone-300 bg-bone-50 p-6 transition hover:-translate-y-0.5 hover:border-forest-950/20 hover:shadow-[0_18px_36px_-28px_rgba(11,26,18,0.5)]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-pine/10 text-pine transition group-hover:bg-pine group-hover:text-bone-50">
                  <Icon className="h-5 w-5" strokeWidth={1.8} />
                </span>
                <h3 className="font-display text-lg font-semibold leading-snug text-forest-950">{service.name}</h3>
                <p className="text-sm leading-relaxed text-forest-950/65">{service.body}</p>
              </li>
            )
          })}
        </ul>

        <div className="mt-8 flex items-center gap-4 rounded-2xl border border-pine/25 bg-gradient-to-r from-pine/10 via-bone-50 to-pine/10 px-6 py-5 shadow-[0_16px_36px_-32px_rgba(11,26,18,0.6)]">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-forest-950 text-bone-50">
            <Check className="h-5 w-5" strokeWidth={2.2} aria-hidden="true" />
          </span>
          <p className="font-display text-lg font-semibold leading-snug text-forest-950 sm:text-xl">
            Free, No-Obligation Written Estimates on All Projects.
          </p>
        </div>
      </div>
    </section>
  )
}

function Gallery() {
  return (
    <section id="gallery" className="scroll-mt-24 border-y border-bone-300 bg-bone-100 py-16 lg:py-24">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-pine">Selected work</p>
            <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-forest-950 sm:text-[2.5rem]">
              Recent Work &amp; Projects
            </h2>
          </div>
          <p className="max-w-xs text-sm leading-relaxed text-forest-950/60">
            Swipe or tap any build to explore multiple angles.
          </p>
        </div>

        <div className="mt-7">
          <ProjectGallery />
        </div>

        <div className="mt-9 flex justify-center">
          <Link
            to="/projects"
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-forest-950/25 bg-white/70 px-7 py-3.5 text-sm font-semibold text-forest-950 shadow-[0_12px_30px_-24px_rgba(11,26,18,0.7)] transition hover:border-forest-950/45 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine focus-visible:ring-offset-2 focus-visible:ring-offset-bone-100"
          >
            View All Projects
            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  )
}

function Contact() {
  return (
    <section id="contact" className="scroll-mt-24 bg-bone-50 py-16 lg:py-24">
      <div className="mx-auto grid max-w-6xl gap-10 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-center lg:gap-14">
        <div className="lg:self-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-pine">Get in touch</p>
          <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-forest-950 sm:text-[2.5rem]">
            Let&rsquo;s Talk About Your Project.
          </h2>
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-forest-950/65">
            Send us a brief overview of your project. We&rsquo;ll follow up by email to discuss details
            and schedule a site visit. Free, no-obligation estimates on all work.
          </p>

          <div className="mt-8 space-y-3">
            <a
              href="tel:2529858235"
              className="flex cursor-pointer items-center gap-3 rounded-2xl border border-bone-300 bg-bone-50 px-5 py-4 transition hover:border-forest-950/20 hover:bg-bone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine focus-visible:ring-offset-2 focus-visible:ring-offset-bone-50"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-pine/10 text-pine">
                <Phone className="h-4 w-4" />
              </span>
              <span>
                <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-950/45">Direct Call</span>
                <span className="mt-0.5 block text-sm font-semibold text-forest-950">{CONTACT.phone}</span>
              </span>
            </a>
            <a
              href="mailto:customercare@trimmedliving.com"
              className="group flex cursor-pointer items-center gap-3 rounded-2xl border border-bone-300 bg-bone-50 px-5 py-4 transition hover:border-forest-950/20 hover:bg-bone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine focus-visible:ring-offset-2 focus-visible:ring-offset-bone-50"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-pine/10 text-pine">
                <Mail className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-950/45">Direct Email</span>
                <span className="mt-0.5 block truncate text-sm font-semibold text-forest-950 underline decoration-transparent underline-offset-4 transition group-hover:decoration-forest-950/30">
                  {CONTACT.email}
                </span>
              </span>
            </a>
          </div>
        </div>

        <div className="rounded-[1.75rem] border border-bone-300 bg-bone-50 p-6 sm:p-8">
          <EstimateForm />
        </div>
      </div>
    </section>
  )
}

function Home() {
  const { hash } = useLocation()

  useEffect(() => {
    if (!hash) return
    const target = document.getElementById(hash.slice(1))
    if (!target) return
    const id = window.requestAnimationFrame(() =>
      target.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    )
    return () => window.cancelAnimationFrame(id)
  }, [hash])

  return (
    <div className="min-h-screen bg-bone-100 font-sans text-forest-950 antialiased">
      <Header />
      <main className="bg-bone-100">
        <Hero />
        <About />
        <Services />
        <Gallery />
        <Contact />
      </main>
      <Footer />
    </div>
  )
}

export default Home
